import React, { useState, useMemo } from "react";
import { Tabs, Table, Tag, Empty, Spin, Collapse } from "antd";
import type { ColumnsType, TabsProps } from "antd";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { useGetServiceTicketsForDashboard } from "~/services/service-ticket";
import { TServiceTicket } from "~/types/tickets";
import { JobStatus } from "~/utils/constants";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

dayjs.extend(isSameOrAfter);

// Helper to calculate dynamic service ticket status
const calculateDynamicStatus = (ticket: TServiceTicket): JobStatus => {
  // Check for completion using both field name variations
  if (ticket.timeOut || ticket.time_out || ticket.completedAt || ticket.completed_at) {
    return JobStatus.COMPLETED;
  }
  // Check for in-progress using both field name variations
  if (ticket.timeIn || ticket.time_in) {
    return JobStatus.IN_PROGRESS;
  }
  return ticket.status || JobStatus.SCHEDULED;
};

export const ServiceStatusOverview: React.FC = () => {
  const [activeTab, setActiveTab] = useState<JobStatus>(JobStatus.SCHEDULED);

  // Fetch service tickets optimized for dashboard (lightweight query)
  // Filter to last 60 days to keep it fast and relevant
  const sixtyDaysAgo = dayjs().subtract(60, "days").startOf('day').toISOString();

  const { data: allServiceTicketsData, isLoading } = useGetServiceTicketsForDashboard({
    page: 1,
    pageSize: 200, // Optimized endpoint with minimal data
    startDate: sixtyDaysAgo,
  });

  // Filter service tickets by calculated status
  const filteredTickets = useMemo(() => {
    if (!allServiceTicketsData?.items) {
      return { draft: [], scheduled: [], inProgress: [], completed: [] };
    }

    const draft: TServiceTicket[] = [];
    const scheduled: TServiceTicket[] = [];
    const inProgress: TServiceTicket[] = [];
    const completed: TServiceTicket[] = [];

    // Filter completed tickets to last 5 days (today + previous 4 days)
    const fiveDaysAgo = dayjs().subtract(4, "days").startOf('day');

    console.log('Service Matrix: Processing', allServiceTicketsData.items.length, 'tickets');
    console.log('Five days ago cutoff:', fiveDaysAgo.format('YYYY-MM-DD'));

    allServiceTicketsData.items.forEach((ticket) => {
      const dynamicStatus = calculateDynamicStatus(ticket);

      switch (dynamicStatus) {
        case JobStatus.DRAFT:
          draft.push(ticket);
          break;
        case JobStatus.SCHEDULED:
          scheduled.push(ticket);
          break;
        case JobStatus.IN_PROGRESS:
          inProgress.push(ticket);
          break;
        case JobStatus.COMPLETED:
          // Only include completed tickets from last 5 days
          // Check all possible completion date fields
          const completedDate = ticket.timeOut || ticket.time_out || ticket.completedAt || ticket.completed_at;
          if (completedDate && dayjs(completedDate).isSameOrAfter(fiveDaysAgo, 'day')) {
            completed.push(ticket);
          } else if (completedDate) {
            console.log('Filtered out completed ticket:', ticket.serial, 'completed on:', dayjs(completedDate).format('YYYY-MM-DD'));
          }
          break;
      }
    });

    console.log('Service Matrix counts:', {
      draft: draft.length,
      scheduled: scheduled.length,
      inProgress: inProgress.length,
      completed: completed.length,
    });

    // Sort all by serviceDatetime (soonest/upcoming first, descending from today)
    const sortByDate = (a: TServiceTicket, b: TServiceTicket) => {
      const dateA = dayjs(a.serviceDatetime || a.startDatetime || a.service_datetime || a.start_datetime);
      const dateB = dayjs(b.serviceDatetime || b.startDatetime || b.service_datetime || b.start_datetime);
      return dateB.valueOf() - dateA.valueOf(); // Descending (latest/soonest on top)
    };

    draft.sort(sortByDate);
    scheduled.sort(sortByDate);
    inProgress.sort(sortByDate);
    completed.sort(sortByDate);

    return { draft, scheduled, inProgress, completed };
  }, [allServiceTicketsData]);

  // Get status badge color
  const getStatusColor = (status: JobStatus) => {
    switch (status) {
      case JobStatus.DRAFT:
        return "default";
      case JobStatus.SCHEDULED:
        return "orange";
      case JobStatus.IN_PROGRESS:
        return "processing";
      case JobStatus.COMPLETED:
        return "success";
      default:
        return "default";
    }
  };

  // Get service type color (matching schedule view colors)
  const getServiceTypeColor = (serviceType: string) => {
    switch (serviceType) {
      case "COLLECTION":
        return "blue";
      case "DELIVERY":
        return "green";
      case "FABRICATION_SERVICE":
        return "purple";
      case "WAREHOUSE_SERVICE":
        return "orange";
      case "SITE_SERVICE":
        return "cyan";
      default:
        return "default";
    }
  };

  // Define tight table columns
  const columns: ColumnsType<TServiceTicket> = [
    {
      title: "Ref #",
      dataIndex: "serial",
      key: "serial",
      width: 100,
      render: (text: string, record: TServiceTicket) => (
        <Link
          to={`/bookings/${record.jobTicket?.id}?tab=services&serviceId=${record.id}`}
          className="text-blue-600 hover:text-blue-800 font-medium text-xs"
        >
          {text}
        </Link>
      ),
    },
    {
      title: "Booking Client",
      key: "bookingClient",
      width: 150,
      render: (_: any, record: TServiceTicket) => (
        <span className="text-xs">
          {record.jobTicket?.client?.companyName ||
            `${record.jobTicket?.client?.firstName || ""} ${record.jobTicket?.client?.lastName || ""}`.trim() ||
            "N/A"}
        </span>
      ),
    },
    {
      title: "Service",
      dataIndex: "serviceType",
      key: "serviceType",
      width: 100,
      render: (type: string) => (
        <Tag color={getServiceTypeColor(type)} className="text-xs">
          {type}
        </Tag>
      ),
    },
    {
      title: "Service Client",
      key: "serviceClient",
      width: 150,
      render: (_: any, record: TServiceTicket) => (
        <span className="text-xs">
          {record.client?.companyName ||
            `${record.client?.firstName || ""} ${record.client?.lastName || ""}`.trim() ||
            "Same"}
        </span>
      ),
    },
    {
      title: "Location",
      key: "location",
      width: 150,
      render: (_: any, record: TServiceTicket) => {
        const location = record.location;
        if (!location) return <span className="text-xs text-gray-400">—</span>;
        return (
          <span className="text-xs">
            {location.city && location.state
              ? `${location.city}, ${location.state}${location.zip ? ` ${location.zip}` : ""}`
              : location.city || location.state || "—"}
          </span>
        );
      },
    },
    {
      title: "Vessel",
      key: "vessel",
      width: 100,
      render: (_: any, record: TServiceTicket) => (
        <span className="text-xs">{record.vessel?.name || "—"}</span>
      ),
    },
    {
      title: "Staff",
      key: "staff",
      width: 70,
      align: "center",
      render: (_: any, record: TServiceTicket) => (
        <Tag color="blue" className="text-xs">
          {(record as any).staffCount ?? record.staffs?.length ?? 0}
        </Tag>
      ),
    },
    {
      title: "Inventory",
      key: "inventory",
      width: 70,
      align: "center",
      render: (_: any, record: TServiceTicket) => (
        <Tag color="purple" className="text-xs">
          {(record as any).inventoryCount ?? record.inventories?.length ?? 0}
        </Tag>
      ),
    },
    {
      title: "Scheduled",
      key: "scheduledDate",
      width: 140,
      render: (_: any, record: TServiceTicket) => (
        <span className="text-xs">
          {record.serviceDatetime
            ? `${dayjs(record.serviceDatetime).format("MMM D, YYYY")} • ${dayjs(record.startDatetime).format("h:mm A")}`
            : "—"}
        </span>
      ),
    },
  ];

  // Get data based on active tab
  const currentTickets = useMemo(() => {
    switch (activeTab) {
      case JobStatus.DRAFT:
        return filteredTickets.draft;
      case JobStatus.SCHEDULED:
        return filteredTickets.scheduled;
      case JobStatus.IN_PROGRESS:
        return filteredTickets.inProgress;
      case JobStatus.COMPLETED:
        return filteredTickets.completed;
      default:
        return filteredTickets.scheduled;
    }
  }, [activeTab, filteredTickets]);

  const items: TabsProps["items"] = [
    {
      key: JobStatus.DRAFT,
      label: (
        <div className="flex items-center space-x-2">
          <span>Draft</span>
          <Tag color="default">{filteredTickets.draft.length}</Tag>
        </div>
      ),
    },
    {
      key: JobStatus.SCHEDULED,
      label: (
        <div className="flex items-center space-x-2">
          <span>Scheduled</span>
          <Tag color="orange">{filteredTickets.scheduled.length}</Tag>
        </div>
      ),
    },
    {
      key: JobStatus.IN_PROGRESS,
      label: (
        <div className="flex items-center space-x-2">
          <span>In Progress</span>
          <Tag color="processing">{filteredTickets.inProgress.length}</Tag>
        </div>
      ),
    },
    {
      key: JobStatus.COMPLETED,
      label: (
        <div className="flex items-center space-x-2">
          <span>Completed (5d)</span>
          <Tag color="success">{filteredTickets.completed.length}</Tag>
        </div>
      ),
    },
  ];

  const collapseItems = [
    {
      key: "1",
      label: (
        <div className="flex items-center justify-between w-full pr-4">
          <div>
            <h2 className="text-lg font-semibold">Service Matrix</h2>
            <p className="text-sm text-gray-500 mt-1">
              Track service tickets by status
            </p>
          </div>
          <div className="flex items-center gap-4">
            {filteredTickets.draft.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Draft:</span>
                <Tag color="default">{filteredTickets.draft.length}</Tag>
              </div>
            )}
            {filteredTickets.scheduled.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Scheduled:</span>
                <Tag color="orange">{filteredTickets.scheduled.length}</Tag>
              </div>
            )}
            {filteredTickets.inProgress.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">In Progress:</span>
                <Tag color="processing">{filteredTickets.inProgress.length}</Tag>
              </div>
            )}
            {filteredTickets.completed.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Completed (5d):</span>
                <Tag color="success">{filteredTickets.completed.length}</Tag>
              </div>
            )}
          </div>
        </div>
      ),
      children: (
        <div>
          <Tabs
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key as JobStatus)}
            items={items}
            className="-mt-4"
          />
          <div className="pb-6">
            <Spin spinning={isLoading}>
              {currentTickets.length === 0 ? (
                <Empty
                  description={`No ${activeTab.toLowerCase().replace(/-/g, " ")} service tickets`}
                  className="py-8"
                />
              ) : (
                <Table
                  columns={columns}
                  dataSource={currentTickets}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  className="border rounded"
                />
              )}
            </Spin>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-lg border">
      <Collapse
        items={collapseItems}
        defaultActiveKey={[]}
        expandIcon={({ isActive }) => (
          <ChevronDown
            size={20}
            className={`transition-transform ${isActive ? "rotate-180" : ""}`}
          />
        )}
        className="bg-transparent border-none"
      />
    </div>
  );
};
